import React, { useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import NukaCarousel from 'nuka-carousel';

import GameCard from './GameSelectionCarousel/GameCard';
import LEFT_NAV_ICON from '../../images/carousel-nav-left.svg';
import RIGHT_NAV_ICON from '../../images/carousel-nav-right.svg';
import moment from '@g4/moment-utc';
import { withValue } from '@g4/automation-hooks';
import tracking from '@g4/g4-analytics';

import { GAME_CARD, PREVIOUS_ARROW, NEXT_ARROW } from './automationHooks.json';
import { useMedia } from '@g4/boneyard/lib/Media';
import {
  defaultSettings,
  mobileDevicesSettings,
} from './GameSelectionCarousel/gameCarouselSettings';
import { OPPONENT_CITY, OPPONENT_NAME } from './constants';

const propTypes = {
  games: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string,
      opponent: PropTypes.shape({
        city: PropTypes.string,
        name: PropTypes.string,
      }),
      showConditions: PropTypes.object,
    })
  ),
  slideIndex: PropTypes.number,
  raidersCityLabel: PropTypes.string,
  raidersTeamNameLabel: PropTypes.string,
  raidersLimitedSeatsLabel: PropTypes.string,
  raidersSoldOutLabel: PropTypes.string,
  raidersVSLabel: PropTypes.string,
  setIsGameChanged: PropTypes.func,
  setIsSold: PropTypes.func,
  gameSelectedLabel: PropTypes.string,
  setStadiumGame: PropTypes.func,
};

const defaultProps = {
  games: [],
  slideIndex: 0,
  raidersCityLabel: 'Las Vegas',
  raidersTeamNameLabel: 'Raiders',
  raidersLimitedSeatsLabel: 'Limited Seats',
  raidersSoldOutLabel: 'Sold Out',
  raidersVSLabel: 'VS',
  gameSelectedLabel: 'Selected',
};

const Container = styled.div`
  width: 100%;
  display: flex;
  position: sticky;

  .slider-frame {
    overflow: visible !important;
  }
`;

const GameCardContainer = styled.div`
  width: 100%;
  display: inline-grid;
  position: sticky;
`;

const MobileControlsContainer = styled.div`
  display: inline-flex;
  margin: 0 auto;
`;

const NavigationContainerMobile = styled.div`
  width: 20%;
  display: flex;
  align-items: center;
  margin-right: ${({ isLeft }) => (isLeft ? '40px' : '0px')};
`;

const NavigationContainer = styled.div`
  width: 10%;
  display: flex;
  align-items: center;
  justify-content: ${({ isLeft }) => (isLeft ? 'flex-end' : 'flex-start')};
  padding-right: 15px;
  padding-left: 15px;
`;

const Carousel = styled(NukaCarousel)`
  border: ${props =>
    props.isMobile ? 'none' : 'solid 1px rgba(151, 151, 151, 0.37)'};
  background-color: rgba(0, 0, 0, 0.2);
  padding: 30px 0;
  overflow: hidden;
  outline: 0 !important;

  .slider-frame > .slider-list > .slider-slide {
    outline: none;
    padding: ${props => (props.isMobile ? '0 10% 0 10% !important' : '0px')};
  }

  .paging-item > button {
    fill: white !important;
    outline: 0;
  }

  .paging-item.active > button {
    fill: blue !important;
  }

  .slider-control-bottomcenter {
    bottom: -15px !important;
  }

  &::before {
    content: 'content';
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    pointer-events: none;
    box-shadow: ${props =>
      props.isMobile
        ? 'none'
        : `inset 250px 0 150px -100px #000000,
      inset -250px 0 150px -100px #000000`};
  }
`;

const CardWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  &:focus {
    outline: none;
  }
`;

const GameSelectionCarousel = ({
  games,
  slideIndex: propsSlideIndex,
  raidersCityLabel,
  raidersTeamNameLabel,
  raidersLimitedSeatsLabel,
  raidersSoldOutLabel,
  raidersVSLabel,
  setIsGameChanged,
  setIsSold,
  gameSelectedLabel,
  setStadiumGame,
}) => {
  const mq = useMedia();
  const isMobile = mq.lte('sm');

  const [slideIndex, setSlideIndex] = useState(propsSlideIndex);
  const [selectedGameIndex, setSelectedGameIndex] = useState(null);
  const [isFirstTimeLoading, setIsFirstTimeLoading] = useState(true);

  const impressionData = currentSlideIndex => {
    return {
      name:
        games[currentSlideIndex]?.showConditions?.[OPPONENT_CITY] +
        ' ' +
        moment(games[currentSlideIndex]?.date).format('MMMM DD, YYYY'),
      creative:
        games[currentSlideIndex]?.showConditions?.[OPPONENT_CITY] +
        ' ' +
        moment(games[currentSlideIndex]?.date).format('MMMM DD, YYYY'),
      position: 'Game Card ' + [currentSlideIndex + 1],
    };
  };

  if (isFirstTimeLoading && games?.length > 0) {
    tracking.trackPromoImpression([impressionData(slideIndex)], null);
    setIsFirstTimeLoading(false);
  }

  const prevSlide = () => setSlideIndex(slideIndex - 1);
  const nextSlide = () => {
    tracking.trackPromoImpression([impressionData(slideIndex + 1)], null);
    setSlideIndex(slideIndex + 1);
  };

  const afterSlide = newSlideIndex => {
    setSlideIndex(newSlideIndex);
  };

  const selectGame = index => {
    setSelectedGameIndex(index);
    tracking.trackPromoClick([impressionData(index)], null);
  };

  const onLeftArrowClick = () => {
    if (slideIndex !== 0 || isMobile) {
      prevSlide();
      tracking.trackEvent(
        'Site Click Tracking',
        'Homepage',
        'Previous Game',
        null
      );
    }
  };

  const onRightArrowClick = () => {
    if (slideIndex !== games.length - 1 || isMobile) {
      nextSlide();
      tracking.trackEvent('Site Click Tracking', 'Homepage', 'Next Game', null);
    }
  };

  return (
    <GameCardContainer>
      <Container>
        {!isMobile && (
          <NavigationContainer isLeft>
            <img
              src={LEFT_NAV_ICON}
              alt=""
              disabled={slideIndex === 0}
              onClick={() => onLeftArrowClick()}
              data-hook={PREVIOUS_ARROW.hook}
            />
          </NavigationContainer>
        )}
        <Carousel
          {...(!isMobile
            ? defaultSettings(slideIndex, afterSlide)
            : mobileDevicesSettings(slideIndex, afterSlide))}
          isMobile={isMobile}
        >
          {games.map(({ date, quantity, showConditions }, index, game) => {
            // past games should not be available for selection
            const pastGame = moment().isAfter(date, 'day');

            const isSoldOut = quantity <= 0 ? true : false;
            const opponentName = showConditions[OPPONENT_NAME];
            const opponentCity = showConditions[OPPONENT_CITY];

            return (
              <CardWrapper
                key={`game_${index}`}
                data-package-id={showConditions.PACKAGE_ID}
                data-hook={withValue(GAME_CARD.hook, index)}
                onClick={() => {
                  setSlideIndex(index);
                  selectGame(index);
                  setStadiumGame(game[index]);
                  setIsGameChanged(selectedGameIndex !== index ? true : false);
                  setIsSold(isSoldOut || pastGame);
                }}
              >
                <GameCard
                  isSoldOut={isSoldOut || pastGame}
                  isLimited={!isSoldOut && quantity <= 50}
                  date={date}
                  opponent={{
                    name: opponentName,
                    city: opponentCity,
                  }}
                  raidersCityLabel={raidersCityLabel}
                  raidersTeamNameLabel={raidersTeamNameLabel}
                  raidersLimitedSeatsLabel={raidersLimitedSeatsLabel}
                  raidersSoldOutLabel={raidersSoldOutLabel}
                  raidersVSLabel={raidersVSLabel}
                  gameSelectedLabel={gameSelectedLabel}
                  selectedGameIndex={selectedGameIndex}
                  highlightedShowIndex={index}
                  automationHook={withValue(GAME_CARD.hook, index)}
                />
              </CardWrapper>
            );
          })}
        </Carousel>
        {!isMobile && (
          <NavigationContainer>
            <img
              src={RIGHT_NAV_ICON}
              alt=""
              onClick={() => onRightArrowClick()}
              data-hook={NEXT_ARROW.hook}
            />
          </NavigationContainer>
        )}
      </Container>
      {isMobile && (
        <MobileControlsContainer>
          <NavigationContainerMobile isLeft>
            <img
              src={LEFT_NAV_ICON}
              alt=""
              disabled={slideIndex === 0}
              onClick={() => onLeftArrowClick()}
              automationHook={PREVIOUS_ARROW.hook}
            />
          </NavigationContainerMobile>
          <NavigationContainerMobile>
            <img
              src={RIGHT_NAV_ICON}
              alt=""
              onClick={() => onRightArrowClick()}
              automationHook={NEXT_ARROW.hook}
            />
          </NavigationContainerMobile>
        </MobileControlsContainer>
      )}
    </GameCardContainer>
  );
};

GameSelectionCarousel.propTypes = propTypes;
GameSelectionCarousel.defaultProps = defaultProps;

export default GameSelectionCarousel;
